/**
 * @module airline
 * Utility functions for airline data processing and formatting
 */

/**
 * Format currency values with proper locale and currency symbol
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code (e.g., 'USD', 'EUR')
 * @param {string} locale - The locale for formatting (default: 'en-US')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD', locale = 'en-US') => {
  if (!amount || isNaN(amount)) return 'N/A'

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  } catch (error) {
    // Fallback formatting
    return `${currency} ${amount.toLocaleString()}`
  }
}

/**
 * Format large numbers with K, M, B suffixes
 * @param {number} num - The number to format
 * @returns {string} Formatted number string
 */
export const formatLargeNumber = (num) => {
  if (!num || isNaN(num)) return 'N/A'

  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'B'
  } else if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}

/**
 * Get status color classes for airline status
 * @param {string} status - The airline status
 * @returns {string} Tailwind CSS classes for the status
 */
export const getStatusColor = (status) => {
  if (!status) return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200'

  const statusLower = status.toLowerCase()

  switch (statusLower) {
    case 'active':
      return 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300'
    case 'inactive':
      return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200'
    case 'suspended':
      return 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300'
    case 'bankrupt':
      return 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300'
    default:
      return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200'
  }
}

/**
 * Get status icon for airline status
 * @param {string} status - The airline status
 * @returns {string} Icon name for the status
 */
export const getStatusIcon = (status) => {
  if (!status) return 'MdHelp'

  const statusLower = status.toLowerCase()

  switch (statusLower) {
    case 'active':
      return 'MdCheckCircle'
    case 'inactive':
      return 'MdPauseCircle'
    case 'suspended':
      return 'MdWarning'
    case 'bankrupt':
      return 'MdCancel'
    default:
      return 'MdHelp'
  }
}

/**
 * Format date to readable string
 * @param {string|Date} date - The date to format
 * @param {string} locale - The locale for formatting (default: 'en-US')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, locale = 'en-US') => {
  if (!date) return 'N/A'

  try {
    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) return 'Invalid Date'

    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(dateObj)
  } catch (error) {
    return 'Invalid Date'
  }
}

/**
 * Format date and time to readable string
 * @param {string|Date} date - The date to format
 * @param {string} locale - The locale for formatting (default: 'en-US')
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date, locale = 'en-US') => {
  if (!date) return 'N/A'

  try {
    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) return 'Invalid Date'

    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj)
  } catch (error) {
    return 'Invalid Date'
  }
}

/**
 * Calculate airline age from founded date
 * @param {string|Date} foundedDate - The airline's founding date
 * @returns {number} Age in years
 */
export const calculateAirlineAge = (foundedDate) => {
  if (!foundedDate) return null

  try {
    const founded = new Date(foundedDate)
    const now = new Date()
    const age = now.getFullYear() - founded.getFullYear()
    const monthDiff = now.getMonth() - founded.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < founded.getDate())) {
      return age - 1
    }

    return age
  } catch (error) {
    return null
  }
}

/**
 * Get safety rating color classes
 * @param {string} rating - The safety rating
 * @returns {string} Tailwind CSS classes for the rating
 */
export const getSafetyRatingColor = (rating) => {
  if (!rating) return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200'

  const ratingLower = rating.toLowerCase()

  switch (ratingLower) {
    case 'excellent':
      return 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300'
    case 'good':
      return 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300'
    case 'satisfactory':
      return 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300'
    case 'poor':
      return 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300'
    default:
      return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200'
  }
}

/**
 * Truncate text to specified length
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

/**
 * Extract domain from URL
 * @param {string} url - The URL to extract domain from
 * @returns {string} Domain name
 */
export const extractDomain = (url) => {
  if (!url) return ''

  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`)
    return urlObj.hostname.replace('www.', '')
  } catch (error) {
    return url
  }
}

/**
 * Validate email format
 * @param {string} email - The email to validate
 * @returns {boolean} Whether the email is valid
 */
export const isValidEmail = (email) => {
  if (!email) return false
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate phone number format
 * @param {string} phone - The phone number to validate
 * @returns {boolean} Whether the phone number is valid
 */
export const isValidPhone = (phone) => {
  if (!phone) return false
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$/
  return phoneRegex.test(phone.replace(/[\s\-()]/g, ''))
}

/**
 * Get airline statistics
 * @param {Array} airlines - Array of airline objects
 * @returns {Object} Statistics object
 */
export const getAirlineStats = (airlines) => {
  if (!airlines || !Array.isArray(airlines)) {
    return {
      total: 0,
      active: 0,
      inactive: 0,
      suspended: 0,
      totalFleetSize: 0,
      totalDestinations: 0,
      totalRevenue: 0,
      averageFleetSize: 0,
      averageRevenue: 0,
    }
  }

  const stats = {
    total: airlines.length,
    active: 0,
    inactive: 0,
    suspended: 0,
    totalFleetSize: 0,
    totalDestinations: 0,
    totalRevenue: 0,
    averageFleetSize: 0,
    averageRevenue: 0,
  }

  airlines.forEach((airline) => {
    // Count by status
    const status = airline.status?.toLowerCase()
    if (status === 'active') stats.active++
    else if (status === 'inactive') stats.inactive++
    else if (status === 'suspended') stats.suspended++

    // Sum fleet size
    stats.totalFleetSize += airline.fleetSize || 0

    // Sum destinations
    stats.totalDestinations += airline.destinations?.length || 0

    // Sum revenue
    stats.totalRevenue += airline.financialInfo?.annualRevenue || 0
  })

  // Calculate averages
  if (stats.total > 0) {
    stats.averageFleetSize = Math.round(stats.totalFleetSize / stats.total)
    stats.averageRevenue = Math.round(stats.totalRevenue / stats.total)
  }

  return stats
}

/**
 * Filter airlines by search term
 * @param {Array} airlines - Array of airline objects
 * @param {string} searchTerm - Search term
 * @returns {Array} Filtered airlines
 */
export const filterAirlinesBySearch = (airlines, searchTerm) => {
  if (!searchTerm || !airlines) return airlines

  const term = searchTerm.toLowerCase()
  return airlines.filter(
    (airline) =>
      airline.name?.toLowerCase().includes(term) ||
      airline.iataCode?.toLowerCase().includes(term) ||
      airline.icaoCode?.toLowerCase().includes(term) ||
      airline.country?.toLowerCase().includes(term) ||
      airline.callsign?.toLowerCase().includes(term) ||
      airline.headquarters?.city?.toLowerCase().includes(term) ||
      airline.headquarters?.country?.toLowerCase().includes(term),
  )
}

/**
 * Filter airlines by status
 * @param {Array} airlines - Array of airline objects
 * @param {string} status - Status filter
 * @returns {Array} Filtered airlines
 */
export const filterAirlinesByStatus = (airlines, status) => {
  if (!status || status === 'all' || !airlines) return airlines

  return airlines.filter((airline) => airline.status?.toLowerCase() === status.toLowerCase())
}

/**
 * Sort airlines by specified field
 * @param {Array} airlines - Array of airline objects
 * @param {string} sortBy - Field to sort by
 * @param {string} sortOrder - Sort order ('asc' or 'desc')
 * @returns {Array} Sorted airlines
 */
export const sortAirlines = (airlines, sortBy = 'name', sortOrder = 'asc') => {
  if (!airlines || !Array.isArray(airlines)) return []

  return [...airlines].sort((a, b) => {
    let aValue, bValue

    switch (sortBy) {
      case 'name':
        aValue = a.name || ''
        bValue = b.name || ''
        break
      case 'fleetSize':
        aValue = a.fleetSize || 0
        bValue = b.fleetSize || 0
        break
      case 'revenue':
        aValue = a.financialInfo?.annualRevenue || 0
        bValue = b.financialInfo?.annualRevenue || 0
        break
      case 'destinations':
        aValue = a.destinations?.length || 0
        bValue = b.destinations?.length || 0
        break
      case 'founded':
        aValue = new Date(a.founded || 0)
        bValue = new Date(b.founded || 0)
        break
      case 'status':
        aValue = a.status || ''
        bValue = b.status || ''
        break
      default:
        aValue = a[sortBy] || ''
        bValue = b[sortBy] || ''
    }

    if (sortOrder === 'desc') {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
    } else {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
    }
  })
}
